/**
 * Выкаченная версия staff SPA. Источник — `window.DOMCRM_APP_VERSION` из runtime `config.js`, который
 * deploy.sh генерирует при каждой выкатке (тег релиза из заголовка release-PR, полный хеш коммита, PR, контур).
 * Фолбэк — Vite define `__BUILD_COMMIT__` (локальная сборка) или «неизвестно».
 */
export type DeployedAppVersion = {
  /** Тег релиза (`v0.18.1`) или null для выкатки PR на demo. */
  tag: string | null;
  /** Полный хеш коммита или null. */
  commit: string | null;
  pr: string | null;
  env: string | null;
  deployedAt: string | null;
};

type RawRuntimeVersion = { tag?: unknown; commit?: unknown; pr?: unknown; env?: unknown; deployed_at?: unknown };

function clean(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t && t !== '-' && t !== 'unknown' ? t : null;
}

function defineCommit(): string | null {
  try {
    return typeof __BUILD_COMMIT__ === 'string' ? clean(__BUILD_COMMIT__) : null;
  } catch {
    return null;
  }
}

export function getDeployedAppVersion(): DeployedAppVersion {
  let raw: RawRuntimeVersion | undefined;
  try {
    raw = typeof window !== 'undefined'
      ? ((window as unknown as { DOMCRM_APP_VERSION?: RawRuntimeVersion }).DOMCRM_APP_VERSION)
      : undefined;
  } catch {
    raw = undefined;
  }
  return {
    tag: clean(raw?.tag),
    commit: clean(raw?.commit) ?? defineCommit(),
    pr: clean(raw?.pr),
    env: clean(raw?.env),
    deployedAt: clean(raw?.deployed_at),
  };
}

/** Короткий хеш коммита для показа пользователю (8 символов). */
export function shortCommit(commit: string | null): string | null {
  return commit ? commit.slice(0, 8) : null;
}

/**
 * Строка версии для окна «Сообщить об ошибке»: `v0.18.1 · коммит 22dfcf94 · prod`;
 * без тега (PR на demo): `коммит 22dfcf94 · demo · PR #102`; ничего не известно — `неизвестна`.
 */
export function formatAppVersion(v: DeployedAppVersion): string {
  const parts: string[] = [];
  if (v.tag) parts.push(v.tag);
  const sc = shortCommit(v.commit);
  if (sc) parts.push(`коммит ${sc}`);
  if (v.env) parts.push(v.env);
  if (!v.tag && v.pr) parts.push(`PR #${v.pr}`);
  return parts.length > 0 ? parts.join(' · ') : 'неизвестна';
}
